import type { MovementCheck } from './shiftMovements'

/**
 * Money that entered a shift through scheduled orders (order_payments):
 * deposits, balances and refunds (negative rows). A pedido's sale belongs to
 * the shift it was created in (orders.shift_id is null on purpose), but its
 * MONEY belongs to the shift each payment landed on — so this is the third
 * source the corte sums, next to orders and check tenders.
 * See database/changes/order_calendar_v1.md, "Corte de caja".
 */
export type BookingPaymentRow = {
  id: number
  booking_id: number | null
  /** YYYY-MM-DD of the booking, for the agenda deep link. */
  fulfillment_date?: string | null
  /** 'cita' opens the citas page instead of the bookings agenda. */
  fulfillment_type?: string | null
  method_name: string
  is_cash: boolean
  amount: number | string
  /** Processor commission snapshot (negative on refunds); absent before the feature. */
  commission_amount?: number | string | null
  created_at: string
}

const num = (v: number | string | null | undefined): number => {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

/**
 * Same shape as `checks_tenders_by_shift` rows, so `checksCashTotal` and the
 * per-method tables consume it unchanged. Refunds subtract; a method whose
 * payments net to zero still appears (a $200 in / $200 out day is a fact).
 */
export function bookingsPaymentBreakdown(rows: BookingPaymentRow[]): Array<{
  payment_type: string
  is_cash: boolean
  amount: number
  check_count: number
}> {
  const byMethod = new Map<string, { is_cash: boolean, amount: number, check_count: number }>()
  for (const r of rows) {
    const key = r.method_name || ''
    const acc = byMethod.get(key) ?? { is_cash: !!r.is_cash, amount: 0, check_count: 0 }
    acc.amount += num(r.amount)
    acc.check_count += 1
    // Snapshot per row; any cash row makes the method cash — same lean as the name lookup.
    acc.is_cash = acc.is_cash || !!r.is_cash
    byMethod.set(key, acc)
  }
  return [...byMethod.entries()].map(([payment_type, v]) => ({
    payment_type,
    is_cash: v.is_cash,
    amount: Math.round(v.amount * 100) / 100,
    check_count: v.check_count,
  }))
}

/**
 * As movement rows for the v2 timeline and its per-method totals. The id is
 * prefixed so `shortId` renders "#P301" (a booking), never a bare number that
 * could be mistaken for a check. Not for the sales KPIs: a deposit is money,
 * not a closed sale.
 */
export function bookingPaymentsAsSales(rows: BookingPaymentRow[]): MovementCheck[] {
  return rows.map(r => ({
    id: `P${r.booking_id ?? r.id}`,
    closed_at: null,
    created_at: r.created_at,
    total: num(r.amount),
    payment_method_name: r.method_name,
    table_number: null,
    booking: r.booking_id != null && r.fulfillment_date ? { id: r.booking_id, date: r.fulfillment_date, cita: r.fulfillment_type === 'cita' } : null,
  }))
}
